import type { UIMessage } from 'ai';
import type { AICodeExecutionFile } from '@jms/model';
import { AGENT_ID, AnyPart, AgentThread, ThreadGroup, ActivityBlockPart, RenderedItem } from './agent.types';

export interface ToolResultSummary {
  headline: string;
  rows: Array<{ label: string; value: string }>;
}

/**
 * Static utility class for the Agent V2 chat.
 * Contains pure helper functions for message normalization, formatting, and grouping.
 */
export class AgentUtility {

  /** Agent identifier for Workbuddy Agent */
  public static readonly AGENT_ID = AGENT_ID;

  /** Month abbreviations for date formatting */
  public static readonly MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'] as const;

  /** Type-safe accessor for properties on union-typed message parts */
  public static partField<T>(part: AnyPart, field: string, fallback: T): T {

    return (field in part) ? (part as Record<string, unknown>)[field] as T : fallback;
  }

  /**
   * Formats tool names to human-readable text.
   * Handles Mastra skill navigation tools (`skill`, `skill_search`, `skill_read`) with
   * purpose-specific labels that reflect completion state; all other tool names are
   * normalised from camelCase/kebab-case.
   * e.g., "search-jobs" -> "Search Jobs", "getJobDetailsTool" -> "Get Job Details"
   */
  public static formatToolName(toolName: string, state?: string): string {

    if (!toolName) return '';
    const isComplete = state === 'output-available' || state === 'output-error' || state === 'output-denied';
    if (toolName === 'skill') return isComplete ? 'Loaded skill' : 'Loading skill';
    if (toolName === 'skill_search') return isComplete ? 'Searched skills' : 'Searching skills';
    if (toolName === 'skill_read') return isComplete ? 'Read skill' : 'Reading skill';
    return toolName
      .replace(/-tool$/i, '')
      .replace(/Tool$/i, '')
      .replace(/-/g, ' ')
      .replace(/([a-z])([A-Z])/g, '$1 $2')
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ')
      .trim();
  }

  /** Check if a MIME type is an image type */
  public static isImageType(mimeType: string): boolean {

    return mimeType.startsWith('image/');
  }

  /** Resolves the full filename from a file part, falling back to providerMetadata if Mastra's V2 round-trip dropped the field. Includes the att_ prefix for internal ID extraction. */
  public static resolveFilename(part: AnyPart): string {

    const filename = AgentUtility.partField(part, 'filename', '');
    if (filename) return filename;
    const wbMeta = AgentUtility.partField(part, 'providerMetadata', null)?.workbuddy;
    return wbMeta?.filename || 'file';
  }

  /** Returns a human-readable display name from a file part, stripping the internal att_{id}__ tracking prefix */
  public static displayFilename(part: AnyPart): string {

    return AgentUtility.displayFilenameFromRaw(AgentUtility.resolveFilename(part));
  }

  /** Removes the internal attachment tracking prefix from a raw filename. */
  public static displayFilenameFromRaw(filename: string): string {

    return filename.replace(/^att_[a-zA-Z0-9_-]+__/, '') || 'file';
  }

  /**
   * Ordered classification rules shared by `getFileIconClass` and `getFileTypeLabel`.
   * Each rule matches by MIME type first, then by filename extension as a fallback when
   * the MIME is generic/unknown (e.g. application/octet-stream). The first matching rule
   * wins, so order matters — keep specific MIME checks (csv, sheet/excel) above generic
   * `text/` and broad family prefixes. `xml` shares the code icon but keeps its own label.
   */
  private static readonly FILE_CLASS_RULES: ReadonlyArray<{ icon: string; label: string; matchMime: (mime: string) => boolean; exts: ReadonlyArray<string> }> = [
    { icon: 'fa-file-pdf', label: 'PDF', matchMime: mime => mime === 'application/pdf', exts: ['pdf'] },
    { icon: 'fa-file-image', label: 'Image', matchMime: mime => mime.startsWith('image/'), exts: ['png', 'jpg', 'jpeg', 'gif', 'webp'] },
    { icon: 'fa-file-video', label: 'Video', matchMime: mime => mime.startsWith('video/'), exts: ['mp4', 'mov'] },
    { icon: 'fa-file-csv', label: 'CSV', matchMime: mime => mime === 'text/csv', exts: ['csv'] },
    { icon: 'fa-file-excel', label: 'Excel', matchMime: mime => mime.includes('sheet') || mime.includes('excel'), exts: ['xlsx', 'xls', 'xlsm'] },
    { icon: 'fa-file-word', label: 'Word', matchMime: mime => mime.includes('word') || mime.includes('document'), exts: ['doc', 'docx'] },
    { icon: 'fa-file-code', label: 'JSON', matchMime: mime => mime === 'application/json', exts: ['json'] },
    { icon: 'fa-file-code', label: 'XML', matchMime: mime => mime.includes('xml'), exts: ['xml'] },
    { icon: 'fa-file-lines', label: 'Text', matchMime: mime => mime.startsWith('text/'), exts: ['txt'] },
  ];

  /** Default classification when no rule matches — generic file icon + label. */
  private static readonly FILE_CLASS_DEFAULT: { icon: string; label: string } = { icon: 'fa-file', label: 'File' };

  /**
   * Single source of truth for file-type classification: resolves the icon + label for a file
   * by MIME type first, then falling back to the filename extension when the MIME is generic or
   * missing. Backs both `getFileIconClass` and `getFileTypeLabel` so icon and label always agree.
   */
  public static classifyFile(mimeType: string, filename?: string): { icon: string; label: string } {

    const mime = (mimeType ?? '').toLowerCase();
    for (const rule of AgentUtility.FILE_CLASS_RULES) {
      if (rule.matchMime(mime)) return { icon: rule.icon, label: rule.label };
    }

    const ext = filename?.split('.').pop()?.toLowerCase();
    if (ext) {
      for (const rule of AgentUtility.FILE_CLASS_RULES) {
        if (rule.exts.includes(ext)) return { icon: rule.icon, label: rule.label };
      }
    }
    return AgentUtility.FILE_CLASS_DEFAULT;
  }

  /**
   * Returns the bare Font Awesome icon name (e.g. `fa-file-pdf`) for a file, classifying by
   * MIME type first and falling back to the filename extension when the MIME is generic or
   * missing. Consumers prepend the style prefix (`fas` / `fa-solid` / `fa-regular`).
   * Single source of truth for file-type icons across the agent UI (chips + cards).
   */
  public static getFileIconClass(mimeType: string, filename?: string): string {

    return AgentUtility.classifyFile(mimeType, filename).icon;
  }

  /**
   * Returns a short human-readable file-type label (e.g. "PDF", "Excel", "Word", "Image"),
   * classifying by MIME type first and falling back to the filename extension. Mirrors the
   * coverage of `getFileIconClass` so icon and label always agree.
   */
  public static getFileTypeLabel(mimeType: string, filename?: string): string {

    return AgentUtility.classifyFile(mimeType, filename).label;
  }

  /** Groups threads into Today / Yesterday / This Week / Earlier buckets */
  public static groupThreadsByDate(threads: AgentThread[]): ThreadGroup[] {

    const now = new Date();
    const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterdayStart = new Date(todayStart.getTime() - 86400000);
    const weekStart = new Date(todayStart.getTime() - todayStart.getDay() * 86400000);

    const groups: Record<string, AgentThread[]> = { Today: [], Yesterday: [], 'This Week': [], Earlier: [] };

    for (const thread of threads) {
      const d = new Date(thread.updatedAt || thread.createdAt);
      if (d >= todayStart) groups['Today'].push(thread);
      else if (d >= yesterdayStart) groups['Yesterday'].push(thread);
      else if (d >= weekStart) groups['This Week'].push(thread);
      else groups['Earlier'].push(thread);
    }

    const byRecent = (a: AgentThread, b: AgentThread) => new Date(b.updatedAt || b.createdAt).getTime() - new Date(a.updatedAt || a.createdAt).getTime();

    return Object.entries(groups)
      .filter(([, list]) => list.length > 0)
      .map(([label, list]) => ({ label, threads: list.sort(byRecent) }));
  }

  public static isToolPartWithCallId(part: AnyPart): boolean {

    return (part.type === 'dynamic-tool' || part.type.startsWith('tool-')) && Boolean(AgentUtility.partField(part, 'toolCallId', ''));
  }

  /** Check if a part is a tool part (dynamic-tool or tool-*) */
  public static isToolPart(part: AnyPart): boolean {

    return part.type === 'dynamic-tool' || part.type.startsWith('tool-');
  }

  /**
   * Check if a tool part is a Mastra skill navigation call.
   * The Mastra workspace skill system injects three built-in tools:
   *   - `skill`        — loads a skill by name
   *   - `skill_search` — searches across skills
   *   - `skill_read`   — reads a skill reference file
   * These appear as regular tool-call parts, distinguished only by their toolName.
   */
  public static isSkillPart(part: AnyPart): boolean {

    if (!AgentUtility.isToolPart(part)) return false;
    const toolName = AgentUtility.partField<string>(part, 'toolName', '');
    return toolName === 'skill' || toolName === 'skill_search' || toolName === 'skill_read';
  }

  /** Special tool parts are rendered outside activity blocks (approval cards, interview, etc.) */
  public static isSpecialToolPart(part: AnyPart): boolean {

    const state = AgentUtility.partField<string>(part, 'state', '');
    // Restored parts arrive as `tool-<name>` with no separate `toolName` field
    // (convertMessages -> AIV5.UI), so derive the canonical name from the type too.
    const toolName = AgentUtility.partField<string>(part, 'toolName', '') || part.type.replace(/^tool-/, '');
    // Interview pauses on approval-requested; the chat container renders the overlay
    // card for that state. Once answered the tool transitions to output-available —
    // we still pull it out of activity blocks so the answer recap badge surfaces.
    if (toolName === 'interview') return state === 'approval-requested' || state === 'output-available';
    if (state === 'approval-requested') return true;
    if (toolName === 'proposeBillScan' && state === 'output-available') return true;
    return false;
  }

  /** Returns the canonical activity block key for collapse/expand state tracking */
  public static activityBlockKey(messageId: string, blockIndex: number): string {

    return `${messageId}-block-${blockIndex}`;
  }

  // ─── Code Execution (hidden) → Intent Steps ──────────────────────────────────

  /**
   * Playful words rotated in the live "working" indicator. They replace any
   * exposure of "code execution" / sandboxing — the user only ever sees Workbuddy Agent
   * doing friendly work. One is picked at random while a block is live.
   */
  public static readonly WORKING_WORDS = ['Cooking', 'Stewing', 'Vibing', 'Analysing', 'Crunching', 'Brewing', 'Noodling', 'Tinkering', 'Churning', 'Percolating', 'Marinating', 'Wrangling', 'Conjuring', 'Simmering', 'Untangling', 'Plotting', 'Number-crunching', 'Working magic'] as const;

  /** Picks a random playful working word (without the trailing ellipsis). */
  public static randomWorkingWord(): string {

    const words = AgentUtility.WORKING_WORDS;
    return words[Math.floor(Math.random() * words.length)];
  }

  /**
   * Detects the Anthropic code-execution wrapper tool. It must never render as a
   * visible "Code_execution" step; instead its inner `toolCalls` become intent steps.
   * Matches both the `tool-code_execution[_*]` part type and a `code_execution` toolName.
   */
  public static isCodeExecutionPart(part: AnyPart): boolean {

    if (!AgentUtility.isToolPart(part)) return false;
    const name = (AgentUtility.partField<string>(part, 'toolName', '') || part.type.replace(/^tool-/, '')).toLowerCase();
    return name === 'code_execution' || name.startsWith('code_execution_');
  }

  /** Safely resolves a tool part's output to an object (it may be a JSON string). */
  public static resolveOutput(part: AnyPart): Record<string, unknown> | null {

    const output = AgentUtility.partField<unknown>(part, 'output', undefined);
    const resolved = typeof output === 'string' ? (() => { try { return JSON.parse(output); } catch { return null; } })() : output;
    return resolved && typeof resolved === 'object' && !Array.isArray(resolved) ? resolved as Record<string, unknown> : null;
  }

  /** Reads the inner programmatic tool calls nested inside a code-execution output. */
  public static codeExecutionToolCalls(part: AnyPart): Array<{ toolName: string; input?: unknown; result?: unknown; error?: string }> {

    const resolved = AgentUtility.resolveOutput(part);
    const calls = resolved?.['toolCalls'];
    return Array.isArray(calls) ? calls as Array<{ toolName: string; input?: unknown; result?: unknown; error?: string }> : [];
  }

  /**
   * True for the per-code-execution tool-call budget guard error (the backend blocks the
   * 16th helper call with "Consecutive tool call limit reached (15/15)..."). This is an internal signal
   * to the model to split work into a new code_execution call, not a user-facing failure, so
   * the UI hides these steps. Backend source: jms-ai code.execution.tool.ts.
   */
  public static isToolCallBudgetError(error?: string): boolean {

    return typeof error === 'string' && error.includes('Consecutive tool call limit reached');
  }

  /** Lowercases + naively pluralizes an entity name for intent labels: "Job" -> "jobs", "WorkOrder" -> "work orders". */
  private static pluralizeEntity(entity: string): string {

    const words = entity.replace(/([a-z])([A-Z])/g, '$1 $2').toLowerCase().trim();
    if (!words) return 'records';
    return words.endsWith('s') ? words : `${words}s`;
  }

  /**
   * Human intent label for a single inner tool call. `queryEntityItems` becomes
   * "Searched {entity}s" from `input.entity`; everything else falls through to the
   * normal tool-name formatter (which already yields "Update Job Description" etc.).
   */
  public static intentLabel(toolName: string, input: unknown): string {

    if (toolName === 'queryEntityItems') {
      const entity = input && typeof input === 'object' ? (input as Record<string, unknown>)['entity'] : undefined;
      return typeof entity === 'string' && entity ? `Searched ${AgentUtility.pluralizeEntity(entity)}` : 'Searched records';
    }
    return AgentUtility.formatToolName(toolName);
  }

  /** Best-effort count meta for an intent step. Spells out rows fetched vs total matched (e.g. "Fetched 50 of 1,000 matching records"), read from the already-summarized result. Empty when not derivable. */
  public static intentMeta(call: { result?: unknown }): string {

    const r = call.result;
    if (!r || typeof r !== 'object') return '';
    const rec = r as Record<string, unknown>;
    const itemCount = typeof rec['itemCount'] === 'number' ? rec['itemCount'] as number : undefined;
    const total = typeof rec['total'] === 'number' ? rec['total'] as number : undefined;
    if (typeof itemCount === 'number' && typeof total === 'number') {
      return total > itemCount
        ? `Fetched ${itemCount.toLocaleString()} of ${total.toLocaleString()} matching records`
        : `Fetched all ${total.toLocaleString()} matching records`;
    }
    if (typeof itemCount === 'number') return `Fetched ${itemCount.toLocaleString()} records`;
    if (typeof total === 'number') return `${total.toLocaleString()} matching records`;
    return '';
  }

  // ─── Readable Tool Output (replaces the raw JSON dump) ───────────────────────

  /** Internal/technical keys never shown to the user. */
  private static readonly OUTPUT_KEY_DENYLIST = new Set(['success', 'exitcode', 'stdout', 'stderr', 'executiontimems', 'errorcode', 'toolcalls', 'sandbox', 'files', 'ok', 'iserror', 'provider', '_id', 'id', 'integration', 'error', 'stack', 'trace', 'searchmethod', 'durationms', 'createdat', 'updatedat', 'stageupdatedat']);

  /**
   * Key-name patterns that signal sensitive data — never shown to the user.
   * Covers secrets (incl. the two-layer `listingToken`), camelCase/snake IDs
   * (`jobId`, `workOrderId`, `_id`), tenant URLs, PII contact fields, and dollar
   * amounts (hidden by product decision — some roles act on invoices without being
   * entitled to see totals). Customer/contact NAMES are intentionally NOT denied.
   */
  private static readonly OUTPUT_KEY_DENY_PATTERN = /token|secret|password|auth|apikey|api_key|signedurl|\bsas\b|jwt|credential|email|phone|mobile|address|street|suburb|postcode|postal|\babn\b|taxid|^id$|_id$|url$|total|subtotal|tax|price|amount|margin|^cost$/i;

  /** Case-sensitive match for camelCase identifier keys (jobId, workOrderId, invoiceId, …). */
  private static readonly OUTPUT_KEY_ID_PATTERN = /Id$/;

  /** Value patterns that signal sensitive data regardless of key — mongo ids, opaque file/artifact ids, signed URLs, JWTs. */
  private static readonly OUTPUT_VALUE_DENY_PATTERN = /^[0-9a-f]{24}$|^(att_|artifact_|file_)|[?&](sig|sas|signature|x-goog-signature|awsaccesskeyid)=|^eyJ[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\./i;

  /** Humanizes an object key for display: "jobNumber" / "job_number" -> "Job Number". */
  private static humanizeKey(key: string): string {

    return key
      .replace(/_/g, ' ')
      .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
      .replace(/\s+/g, ' ')
      .trim()
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }

  /**
   * Reusable, leak-safe renderer for any tool output that previously dumped JSON.
   * Walks only top-level scalar fields (no recursion into nested objects/arrays,
   * which can hide IDs, raw query internals, or other-record data), drops denylisted
   * and sensitive keys, formats booleans, truncates long values, and caps rows.
   * Read ~5% of the time, so it deliberately shows LESS rather than risk a leak.
   */
  public static summarizeToolResult(output: unknown): ToolResultSummary {

    const resolved = typeof output === 'string' ? (() => { try { return JSON.parse(output); } catch { return output; } })() : output;

    if (typeof resolved === 'string') {
      const text = resolved.trim();
      return { headline: '', rows: text ? [{ label: 'Result', value: text.length > 160 ? `${text.slice(0, 160)}…` : text }] : [] };
    }
    if (!resolved || typeof resolved !== 'object' || Array.isArray(resolved)) return { headline: '', rows: [] };

    const record = resolved as Record<string, unknown>;
    const messageEntry = Object.entries(record).find(([key]) => key.toLowerCase() === 'message');
    const rawHeadline = typeof messageEntry?.[1] === 'string' ? messageEntry[1].trim() : '';
    const headline = rawHeadline && !AgentUtility.OUTPUT_VALUE_DENY_PATTERN.test(rawHeadline)
      ? (rawHeadline.length > 200 ? `${rawHeadline.slice(0, 200)}…` : rawHeadline)
      : '';

    const rows: Array<{ label: string; value: string }> = [];

    // Written line items (addLineItemsToJob readback) would be dropped by the
    // scalar-only walk below; render them as one readable row from an explicit
    // allowlist of fields — same leak stance as the rest of this method.
    const addedRaw = record.added;
    if (Array.isArray(addedRaw) && addedRaw.length > 0) {
      const lines = addedRaw.slice(0, 8).map((item) => {
        if (!item || typeof item !== 'object') return null;
        const record = item as Record<string, unknown>;
        if (typeof record.code !== 'string' || !record.code) return null;
        const qty = typeof record.quantity === 'number' ? `${record.quantity} × ` : '';
        const stage = typeof record.stage === 'string' && record.stage ? ` → ${record.stage}` : '';
        const outcome = record.stageOutcome === 'new' ? ' (new stage)' : record.stageOutcome === 'renamed-default' ? ' (renamed default stage)' : '';
        const sell = typeof record.sellPrice === 'number' ? ` — $${record.sellPrice.toFixed(2)}` : '';
        return `${qty}${record.code}${stage}${outcome}${sell}`;
      }).filter((line): line is string => !!line);
      if (lines.length > 0) rows.push({ label: 'Added', value: lines.join('\n') });
    }

    for (const [key, value] of Object.entries(record)) {
      if (rows.length >= 8) break;
      const lowerKey = key.toLowerCase();
      if (lowerKey === 'message') continue;
      if (AgentUtility.OUTPUT_KEY_DENYLIST.has(lowerKey) || AgentUtility.OUTPUT_KEY_DENY_PATTERN.test(key) || AgentUtility.OUTPUT_KEY_ID_PATTERN.test(key)) continue;
      if (value == null || typeof value === 'object') continue;
      const formatted = typeof value === 'boolean' ? (value ? 'Yes' : 'No') : String(value);
      const trimmed = formatted.trim();
      if (!trimmed || AgentUtility.OUTPUT_VALUE_DENY_PATTERN.test(trimmed)) continue;
      const display = (typeof value === 'string' ? AgentUtility.formatIsoDate(trimmed) : null) ?? trimmed;
      rows.push({ label: AgentUtility.humanizeKey(key), value: display.length > 120 ? `${display.slice(0, 120)}…` : display });
    }
    return { headline, rows };
  }

  /** Formats an ISO instant as a readable browser-local date/time without shifting date-only midnight values. */
  private static formatIsoDate(value: string): string | null {

    const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(value);
    if (!match) return null;
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return null;

    if (match[4] === '00' && match[5] === '00') {
      const calendarDate = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
      return calendarDate.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
    }

    return parsed.toLocaleString(undefined, { day: 'numeric', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit' });
  }

  /** Minimum text length to be considered a "real" response that breaks an activity group. */
  private static readonly ACTIVITY_TEXT_BREAK_THRESHOLD = 60;

  /**
   * Groups message parts into rendered items. Consecutive reasoning + tool parts
   * are collected into activity blocks. Text parts break groups only if they contain
   * substantial content (>60 chars) — short connector text between tool calls is
   * absorbed into the activity block to avoid fragmenting a single work sequence.
   */
  public static groupMessageParts(parts: AnyPart[]): RenderedItem[] {

    const result: RenderedItem[] = [];
    let accumulator: ActivityBlockPart[] = [];
    let blockIndex = 0;

    const flushAccumulator = () => {
      if (accumulator.length > 0) {
        result.push({ kind: 'activity-block', parts: accumulator, blockIndex });
        blockIndex++;
        accumulator = [];
      }
    };

    for (let i = 0; i < parts.length; i++) {
      const part = parts[i];

      if (part.type === 'text') {
        const text = AgentUtility.partField(part, 'text', '').trim();
        const hasActivityContext = accumulator.length > 0;
        const hasMoreActivity = parts.slice(i + 1).some(p => p.type === 'reasoning' || (AgentUtility.isToolPart(p) && !AgentUtility.isSpecialToolPart(p)));

        if (hasActivityContext && hasMoreActivity && text.length < AgentUtility.ACTIVITY_TEXT_BREAK_THRESHOLD) {
          accumulator.push({ part, partIndex: i });
        } else {
          flushAccumulator();
          result.push({ kind: 'text', part, partIndex: i });
        }
      } else if (part.type === 'file') {
        result.push({ kind: 'file', part, partIndex: i });
      } else if (AgentUtility.isToolPart(part) && AgentUtility.isSpecialToolPart(part)) {
        flushAccumulator();
        result.push({ kind: 'special-tool', part, partIndex: i });
      } else if (part.type === 'reasoning' || AgentUtility.isToolPart(part)) {
        accumulator.push({ part, partIndex: i });
      }
    }

    flushAccumulator();
    return result;
  }

  /**
   * Returns a summary label for a completed activity block, e.g. "Thinking + 4 steps".
   * Code-execution parts are hidden and counted by their inner intent steps, so the
   * count reflects what the user actually sees rather than raw "Code_execution" calls.
   */
  public static activityBlockSummaryLabel(parts: ActivityBlockPart[]): string {

    const hasThinking = parts.some(p => p.part.type === 'reasoning');
    const toolParts = parts.filter(p => AgentUtility.isToolPart(p.part));
    const skillCount = toolParts.filter(p => AgentUtility.isSkillPart(p.part)).length;

    let stepCount = 0;
    for (const p of toolParts) {
      if (AgentUtility.isSkillPart(p.part)) continue;
      if (AgentUtility.isCodeExecutionPart(p.part)) stepCount += Math.max(1, AgentUtility.codeExecutionToolCalls(p.part).length);
      else stepCount += 1;
    }

    const segments: string[] = [];
    if (hasThinking) segments.push('Thinking');
    if (skillCount > 0) segments.push(`${skillCount} skill${skillCount > 1 ? 's' : ''}`);
    if (stepCount > 0) segments.push(`${stepCount} step${stepCount > 1 ? 's' : ''}`);

    if (segments.length === 0) return 'Activity';
    if (segments.length === 1 && !hasThinking) return `${segments[0]} used`;
    return segments.join(' + ');
  }

  /** Returns a live label for a streaming activity block based on the last part */
  public static activityBlockLiveLabel(parts: ActivityBlockPart[]): string {

    if (parts.length === 0) return 'Working...';
    const last = parts[parts.length - 1];
    if (last.part.type === 'reasoning') return 'Thinking...';
    const toolName = AgentUtility.partField<string>(last.part, 'toolName', '');
    if (AgentUtility.isSkillPart(last.part)) {
      if (toolName === 'skill') return 'Loading skill...';
      if (toolName === 'skill_search') return 'Searching skills...';
      if (toolName === 'skill_read') return 'Reading skill...';
    }
    if (toolName) return `${AgentUtility.formatToolName(toolName)}...`;
    return 'Working...';
  }

  public static isTerminalToolState(state: string): boolean {

    return state === 'output-available' || state === 'output-error' || state === 'output-denied' || state === 'approval-responded' || state === 'result';
  }

  /**
   * Sanitize messages before they are sent to the backend via the transport.
   * Resolves any non-terminal tool parts to output-error so the Anthropic API
   * never sees a tool_use block without a matching tool_result.
   *
   * This runs inside prepareSendMessagesRequest, which fires on EVERY request
   * (user-initiated sends AND auto-resubmits from sendAutomaticallyWhen).
   */
  public static sanitizeOutboundMessages(messages: UIMessage[]): UIMessage[] {

    return messages.map((msg) => {
      if (msg.role !== 'assistant') return msg;
      let changed = false;
      const parts = msg.parts.map((part) => {
        if (!AgentUtility.isToolPartWithCallId(part)) return part;
        const state = AgentUtility.partField<string>(part, 'state', '');
        if (AgentUtility.isTerminalToolState(state)) return part;
        changed = true;
        return { ...part, state: 'output-error', errorText: 'Skipped — interrupted or superseded' } as typeof part;
      });
      return changed ? { ...msg, parts } : msg;
    });
  }

  /**
   * Resolve tool parts from restored messages into clean terminal states.
   *
   * When a stream is interrupted (user navigates away, server error, etc.), tool parts can be
   * stuck in non-terminal states (input-streaming, input-available, approval-requested, etc.).
   * Mastra restores these as `input-available` (for unresolved calls) or `output-available` (resolved).
   * Non-terminal states create invalid messages for the Anthropic API: tool_use without tool_result.
   *
   * Strategy:
   * - Terminal states: keep as-is
   * - Non-terminal with a terminal sibling (same toolCallId): drop (it's an intermediate duplicate)
   * - Non-terminal with NO terminal sibling (orphan): resolve first to output-error, drop rest
   */
  public static collapseRestoredToolStates(messages: UIMessage[]): UIMessage[] {

    const terminalToolIds = new Set<string>();
    for (const message of messages) {
      for (const part of message.parts) {
        if (!AgentUtility.isToolPartWithCallId(part)) continue;
        const toolCallId = AgentUtility.partField(part, 'toolCallId', '');
        const state = AgentUtility.partField(part, 'state', '');
        if (toolCallId && AgentUtility.isTerminalToolState(state)) terminalToolIds.add(toolCallId);
      }
    }

    const resolvedOrphans = new Set<string>();

    return messages.flatMap((message) => {
      let changed = false;
      const resultParts = message.parts.flatMap((part) => {
        if (!AgentUtility.isToolPartWithCallId(part)) return [part];
        const toolCallId = AgentUtility.partField(part, 'toolCallId', '');
        const state = AgentUtility.partField<string>(part, 'state', '');

        if (AgentUtility.isTerminalToolState(state)) return [part];

        if (terminalToolIds.has(toolCallId)) {
          changed = true;
          return [];
        }

        // A restored approval-requested part whose run is still resumable (approval.runId present) is a
        // genuinely pending approval, NOT an abandoned orphan: switching surfaces (popup<->fullscreen)
        // cold-restores the thread and would otherwise collapse a live approval to a false 'declined'.
        // Keep it actionable — the adapter rehydrates toolCallId->runId from this same approval.runId.
        if (state === 'approval-requested') {
          const approval = AgentUtility.partField<{ runId?: string } | null>(part, 'approval', null);
          if (approval?.runId) return [part];
        }

        if (resolvedOrphans.has(toolCallId)) {
          changed = true;
          return [];
        }
        resolvedOrphans.add(toolCallId);
        changed = true;
        // Any tool part left NON-terminal on restore is an abandoned/un-actioned run — a pending
        // approval the user never answered (e.g. refreshed/closed the tab), or an in-flight call cut
        // off by a refresh. Collapse it to the calm, non-error 'output-denied' state instead of the red
        // "Session interrupted" — the action simply never completed, which the user reads as a rejection.
        // (Genuine tool FAILURES are terminal 'output-error' and never reach this orphan branch.)
        return [{ ...part, state: 'output-denied', errorText: '' } as typeof part];
      });

      if (resultParts.length === 0) return [];
      if (!changed) return [message];
      return [{ ...message, parts: resultParts }];
    });
  }

  /** Converts backend AIUIMessage[] to the UIMessage shape the Chat class expects */
  public static convertToUIMessages(rawMessages: Array<{ id?: string; role: string; parts?: unknown[]; content?: string; createdAt?: string; metadata?: Record<string, unknown> }>): UIMessage[] {

    const messages = (rawMessages ?? []).map(msg => ({
      id: msg.id ?? crypto.randomUUID(),
      role: msg.role as 'user' | 'assistant',
      parts: (msg.parts ?? (msg.content ? [{ type: 'text' as const, text: msg.content }] : [])) as unknown as UIMessage['parts'],
      metadata: msg.metadata,
      createdAt: msg.createdAt ? new Date(msg.createdAt) : (typeof msg.metadata?.['createdAt'] === 'string' ? new Date(msg.metadata['createdAt']) : new Date()),
    }));

    return AgentUtility.normalizeRestoredMessages(messages);
  }

  private static normalizePartForFingerprint(part: AnyPart): unknown {

    if (part.type === 'text') return { type: 'text', text: AgentUtility.partField(part, 'text', '') };
    if (part.type === 'reasoning') return { type: 'reasoning', text: AgentUtility.partField(part, 'text', '') };
    if (part.type === 'file') return { type: 'file', url: AgentUtility.partField(part, 'url', '') || AgentUtility.partField(part, 'data', ''), mediaType: AgentUtility.partField(part, 'mediaType', '') || AgentUtility.partField(part, 'mimeType', ''), filename: AgentUtility.partField(part, 'filename', '') };
    if (part.type === 'dynamic-tool' || part.type.startsWith('tool-')) {
      return {
        type: part.type,
        toolCallId: AgentUtility.partField(part, 'toolCallId', ''),
        state: AgentUtility.partField(part, 'state', ''),
        input: AgentUtility.partField(part, 'input', undefined),
        output: AgentUtility.partField(part, 'output', undefined),
        errorText: AgentUtility.partField(part, 'errorText', ''),
      };
    }
    return part;
  }

  private static fingerprintMessage(message: UIMessage): string {

    return JSON.stringify({
      role: message.role,
      parts: message.parts.map(part => AgentUtility.normalizePartForFingerprint(part)),
    });
  }

  private static deduplicateConsecutiveMessages(messages: UIMessage[]): UIMessage[] {

    const deduped: UIMessage[] = [];

    for (const message of messages) {
      const previous = deduped[deduped.length - 1];
      if (previous && AgentUtility.fingerprintMessage(previous) === AgentUtility.fingerprintMessage(message)) continue;
      deduped.push(message);
    }

    return deduped;
  }

  private static normalizeRestoredMessages(messages: UIMessage[]): UIMessage[] {

    const collapsed = AgentUtility.collapseRestoredToolStates(messages);
    const withErrorStates = AgentUtility.detectToolErrorOutputs(collapsed);
    return AgentUtility.deduplicateConsecutiveMessages(withErrorStates);
  }

  /**
   * Detect tool outputs that contain error messages and fix their state.
   * Mastra stores failed tools with state: 'output-available' but error text in the output field.
   */
  private static detectToolErrorOutputs(messages: UIMessage[]): UIMessage[] {

    return messages.map((message) => {
      if (message.role !== 'assistant') return message;

      let changed = false;
      const fixedParts = message.parts.map((part) => {
        if (!AgentUtility.isToolPart(part)) return part;

        const state = AgentUtility.partField<string>(part, 'state', '');
        if (state !== 'output-available') return part;

        const output = AgentUtility.partField<unknown>(part, 'output', null);
        if (!AgentUtility.isErrorOutput(output)) return part;

        changed = true;
        return { ...part, state: 'output-error', errorText: AgentUtility.extractErrorText(output) } as typeof part;
      });

      return changed ? { ...message, parts: fixedParts } : message;
    });
  }

  /** Check if tool output contains error patterns */
  private static isErrorOutput(output: unknown): boolean {

    if (typeof output === 'string') {
      const lower = output.toLowerCase();
      return lower.includes('not found') || lower.includes('error:') || lower.includes('failed:') || lower.startsWith('error') || lower.includes('exception');
    }
    if (output && typeof output === 'object') {
      const obj = output as Record<string, unknown>;
      if (obj['error'] || obj['isError'] === true || obj['ok'] === false) return true;
    }
    return false;
  }

  /** Extract error text from output */
  private static extractErrorText(output: unknown): string {

    if (typeof output === 'string') return output;
    if (output && typeof output === 'object') {
      const obj = output as Record<string, unknown>;
      return String(obj['error'] ?? obj['message'] ?? obj['errorText'] ?? 'Tool execution failed');
    }
    return 'Tool execution failed';
  }

  // ─── File Extraction ───────────────────────────────────────────────────────

  /** MIME type mapping for common file extensions */
  private static readonly MIME_MAP: Record<string, string> = { png: 'image/png', jpg: 'image/jpeg', jpeg: 'image/jpeg', gif: 'image/gif', pdf: 'application/pdf', csv: 'text/csv', json: 'application/json', txt: 'text/plain', xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', xls: 'application/vnd.ms-excel', docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' };

  /** Regex patterns for extracting filenames from stdout text */
  private static readonly STDOUT_FILENAME_PATTERNS: RegExp[] = [/at:\s*\/[^\s]*\/([^\s/]+\.[a-z0-9]+)/i, /saved\s+(?:as|to)\s+([^\s]+\.[a-z0-9]+)/i, /output:\s*([^\s]+\.[a-z0-9]+)/i, /file:\s*([^\s]+\.[a-z0-9]+)/i];

  /** Collect downloadable files from selected parts while sharing message-level deduplication state. */
  public static collectDownloadFilesFromParts(parts: AnyPart[], seen: Set<string>): AICodeExecutionFile[] {

    const files: AICodeExecutionFile[] = [];
    for (const part of parts) {
      if (!AgentUtility.isToolPart(part)) continue;
      if (AgentUtility.partField<string>(part, 'state', '') !== 'output-available') continue;
      const output = AgentUtility.partField<unknown>(part, 'output', undefined);
      for (const file of AgentUtility.extractDownloadFiles(output)) {
        if (seen.has(file.fileId)) continue;
        seen.add(file.fileId);
        files.push(file);
      }
    }
    return files;
  }

  /** Extract downloadable file references from tool output data */
  public static extractDownloadFiles(result: unknown): AICodeExecutionFile[] {

    const files: AICodeExecutionFile[] = [];
    const resolved = typeof result === 'string' ? (() => { try { return JSON.parse(result); } catch { return result; } })() : result;
    const stdout = resolved && typeof resolved === 'object' ? (resolved as Record<string, unknown>)['stdout'] as string | undefined : undefined;
    const fallback = AgentUtility.extractFilenameFromStdout(stdout);

    const collectItems = (items: unknown) => {
      if (!Array.isArray(items)) return;
      for (const item of items) {
        if (!item || typeof item !== 'object') continue;
        const r = item as Record<string, unknown>;
        AgentUtility.addFileEntry(files, r.file_id ?? r.fileId, r.filename, r.mime_type ?? r.mimeType ?? r.mime, fallback);
      }
    };

    if (resolved && typeof resolved === 'object') {
      const record = resolved as Record<string, unknown>;
      collectItems(record['content']);
      collectItems(record['files']);
    }
    if (files.length === 0) AgentUtility.scanForFiles(resolved, files, fallback);
    return files;
  }

  private static addFileEntry(files: AICodeExecutionFile[], fileId: unknown, filename: unknown, mimeType: unknown, fallback: string | null): void {

    if (typeof fileId !== 'string' || (!fileId.startsWith('file_') && !fileId.startsWith('artifact_'))) return;
    const name = typeof filename === 'string' && filename ? filename : fallback || 'download';
    files.push({ fileId, filename: name, mimeType: typeof mimeType === 'string' && mimeType ? mimeType : AgentUtility.inferMimeType(name) });
  }

  private static scanForFiles(value: unknown, files: AICodeExecutionFile[], fallback: string | null, seen = new Set<object>(), depth = 0): void {

    if (!value || typeof value !== 'object' || depth > 6 || seen.has(value)) return;
    seen.add(value);
    if (Array.isArray(value)) { for (const item of value) AgentUtility.scanForFiles(item, files, fallback, seen, depth + 1); return; }
    const r = value as Record<string, unknown>;
    AgentUtility.addFileEntry(files, r.file_id ?? r.fileId, r.filename, r.mime_type ?? r.mimeType ?? r.mime, fallback);
    for (const child of Object.values(r)) AgentUtility.scanForFiles(child, files, fallback, seen, depth + 1);
  }

  private static extractFilenameFromStdout(stdout?: string): string | null {

    if (!stdout) return null;
    for (const p of AgentUtility.STDOUT_FILENAME_PATTERNS) {
      const m = stdout.match(p);
      if (m?.[1]) return m[1];
    }
    return null;
  }

  public static inferMimeType(filename: string): string | undefined {

    const ext = filename.split('.').pop()?.toLowerCase();
    return ext ? AgentUtility.MIME_MAP[ext] : undefined;
  }
}
